import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { acquireSharedRealtimeChannel } from "@/lib/sharedRealtimeChannel";
import { dismissLeftSquad, updateLeftSquad } from "@/lib/leftSquads";
import { onSquadJoined } from "@/lib/squad";
import type { RejoinRequestStatus } from "@/lib/rejoinRequests";
import i18n from "@/lib/i18next";

const DEDUPE_MS = 2_000;

type RejoinRequestRow = {
  id: string;
  squad_id: string;
  user_id: string;
  status: RejoinRequestStatus;
};

export function useRejoinRequestSync() {
  const { user, isGuest, hydrated } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const handledAtRef = useRef<Map<string, number>>(new Map());
  const userId = user && !isGuest ? user.id : undefined;

  useEffect(() => {
    if (!hydrated || !userId) return;

    const shouldHandle = (requestId: string): boolean => {
      const now = Date.now();
      const last = handledAtRef.current.get(requestId);
      if (last != null && now - last < DEDUPE_MS) return false;
      handledAtRef.current.set(requestId, now);
      return true;
    };

    const handleUpdate = async (row: RejoinRequestRow) => {
      if (!shouldHandle(row.id)) return;

      if (row.status === "approved") {
        await onSquadJoined(queryClient, userId, row.squad_id);
        dismissLeftSquad(row.squad_id);
        toast.success(i18n.t("consumer:squad.rejoinApprovedToast"));
        router.navigate({ to: "/squad/$id", params: { id: row.squad_id } });
        return;
      }

      if (row.status === "denied") {
        updateLeftSquad({
          id: row.squad_id,
          pendingRequestId: null,
          reason: "self_leave",
        });
        toast.info(i18n.t("consumer:squad.rejoinDeniedToast"));
        return;
      }

      if (row.status === "expired") {
        updateLeftSquad({
          id: row.squad_id,
          pendingRequestId: null,
          reason: "self_leave",
        });
        toast.info(i18n.t("consumer:squad.rejoinExpiredToast"));
      }
    };

    const topic = `rejoin-request-sync-${userId}`;
    return acquireSharedRealtimeChannel(
      topic,
      (broadcast) =>
        supabase
          .channel(topic)
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "squad_rejoin_requests",
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              const row = payload.new as RejoinRequestRow;
              if (!row?.id || !row.squad_id) return;
              void handleUpdate(row);
              broadcast();
            },
          )
          .subscribe(),
      () => {
        /* Primary work runs in channel handlers. */
      },
    );
  }, [hydrated, userId, queryClient, router]);
}
